<template>
  <div>
    <div class="flex items-center space-x-4">
      <router-link
        :to="{ name: isEditing ? 'productsDetail' : 'productsIndex' }"
        class="system-button"
      >
        <arrow-icon class="w-4 rotate-90"></arrow-icon>
      </router-link>
      <h2>{{ isEditing ? "Editar" : "Crear" }} Producto</h2>
    </div>

    <div v-if="!loading" class="card">
      <form v-if="productCategories" @submit.prevent="save">
        <div class="flex flex-col w-full">
          <label for="sku">SKU</label>
          <input id="sku" type="text" v-model="form.sku" />
          <validation-errors :errors="errorFor('sku')"></validation-errors>
        </div>

        <div class="flex flex-col w-full mt-6">
          <label for="name">Nombre</label>
          <input id="name" type="text" v-model="form.name" />
          <validation-errors :errors="errorFor('name')"></validation-errors>
        </div>

        <div class="flex flex-col w-full mt-6">
          <label for="description">Descripción</label>
          <textarea
            class="h-28"
            id="description"
            type="text"
            v-model="form.description"
          />
          <validation-errors
            :errors="errorFor('description')"
          ></validation-errors>
        </div>

        <div class="flex flex-col w-full mt-6">
          <label for="unit_price">Precio unitario</label>
          <input
            id="unit_price"
            type="number"
            step="any"
            v-model="form.unit_price"
          />
          <validation-errors
            :errors="errorFor('unit_price')"
          ></validation-errors>
        </div>

        <label for="category" class="mt-6">Categorías</label>
        <div class="grid grid-cols-1">
          <div
            v-for="category in productCategories"
            :key="category.id"
            class="flex items-center mt-2 mr-8"
          >
            <input
              :id="`category-${category.id}`"
              type="checkbox"
              class="h-5 w-5"
              v-model="form.categoryIds[category.id]"
            />
            <span :for="`category-${category.id}`" class="ml-2">{{ category.name }}</span>
          </div>
        </div>
        <validation-errors
          :errors="errorFor('productCategories')"
        ></validation-errors>

        <!-- SEO section -->
        <h3 class="font-medium mt-8">Detalles SEO</h3>
        <div class="div-line"></div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div class="flex flex-col">
            <label for="slug">Slug</label>
            <input
              id="slug"
              type="text"
              class="font-mono"
              v-model="form.slug"
              placeholder="ej: croquetas-pollo-cachorro"
            />
            <p class="text-xs text-content-300 mt-1">
              Solo letras minúsculas, números y guiones.
            </p>
            <validation-errors :errors="errorFor('slug')"></validation-errors>
          </div>

          <div class="flex items-center mt-2 md:mt-6">
            <input
              id="is_published"
              type="checkbox"
              class="h-5 w-5"
              v-model="form.is_published"
            />
            <label for="is_published" class="ml-2">Publicado</label>
          </div>
        </div>

        <!-- Dimensions / weight section -->
        <h3 class="font-medium mt-8">Dimensiones y peso</h3>
        <div class="div-line"></div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
          <div class="flex flex-col">
            <label for="weight_grams">Peso (g)</label>
            <input
              id="weight_grams"
              type="number"
              min="0"
              v-model="form.weight_grams"
            />
            <validation-errors
              :errors="errorFor('weight_grams')"
            ></validation-errors>
          </div>
          <div class="flex flex-col">
            <label for="length_cm">Largo (cm)</label>
            <input
              id="length_cm"
              type="number"
              step="any"
              min="0"
              v-model="form.length_cm"
            />
          </div>
          <div class="flex flex-col">
            <label for="width_cm">Ancho (cm)</label>
            <input
              id="width_cm"
              type="number"
              step="any"
              min="0"
              v-model="form.width_cm"
            />
          </div>
          <div class="flex flex-col">
            <label for="height_cm">Alto (cm)</label>
            <input
              id="height_cm"
              type="number"
              step="any"
              min="0"
              v-model="form.height_cm"
            />
          </div>
        </div>

        <div class="flex items-center mt-4">
          <input
            id="allow_backorder"
            type="checkbox"
            class="h-5 w-5"
            v-model="form.allow_backorder"
          />
          <label for="allow_backorder" class="ml-2">
            Permitir backorder (vender sin stock)
          </label>
        </div>

        <!-- Tags section -->
        <h3 class="font-medium mt-8">Etiquetas</h3>
        <div class="div-line"></div>

        <div class="flex flex-wrap gap-2 mt-2">
          <label
            v-for="tag in tags"
            :key="tag.id"
            :class="[
              'cursor-pointer px-3 py-1 rounded-full border text-sm',
              form.tag_ids.includes(tag.id)
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white border-content-100 text-content-500',
            ]"
          >
            <input
              type="checkbox"
              class="hidden"
              :value="tag.id"
              v-model="form.tag_ids"
            />
            {{ tag.name }}
          </label>
          <p v-if="!tags.length" class="text-content-300 text-sm">
            No hay etiquetas creadas.
          </p>
        </div>

        <!-- Videos section -->
        <h3 class="font-medium mt-8">Videos</h3>
        <div class="div-line"></div>

        <div class="mt-2 space-y-2">
          <div
            v-for="(_, index) in form.video_urls"
            :key="index"
            class="flex space-x-2 items-center"
          >
            <input
              type="url"
              v-model="form.video_urls[index]"
              placeholder="https://..."
              class="flex-1"
            />
            <button
              type="button"
              class="delete-button"
              @click="removeVideoUrl(index)"
            >
              Quitar
            </button>
          </div>
          <button
            type="button"
            class="secondary-button"
            @click="addVideoUrl"
          >
            + Agregar URL de video
          </button>
        </div>

        <primary-button type="submit" class="mt-8 mx-0" :loading="loadingForm"
          >{{ isEditing ? "Guardar" : "Crear" }}
        </primary-button>
      </form>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
export default {
  name: "Product Form",

  data() {
    return {
      form: {
        sku: "",
        name: "",
        description: "",
        unit_price: null,
        categoryIds: {},
        slug: "",
        is_published: false,
        weight_grams: null,
        length_cm: null,
        width_cm: null,
        height_cm: null,
        allow_backorder: false,
        tag_ids: [],
        video_urls: [],
      },
      loading: false,
      loadingForm: false,
      isEditing: false,
      productCategories: null,
      tags: [],
    };
  },

  created() {
    this.$store.commit("setStoreSelectedTab", "products");

    this.loadProductCategories();
    this.loadTags();

    if (this.$route.params.id) {
      this.isEditing = true;
      this.loadProduct();
    }
  },

  mixins: [validationErrors],

  methods: {
    async loadProductCategories() {
      this.loading = true;

      try {
        this.productCategories = await api.productCategories.getAll();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadTags() {
      try {
        this.tags = await api.tags.getAll();
      } catch (error) {
        // Tags may not be accessible for all roles; degrade silently.
        this.tags = [];
      }
    },

    addVideoUrl() {
      this.form.video_urls.push("");
    },

    removeVideoUrl(index) {
      this.form.video_urls.splice(index, 1);
    },

    async loadProduct() {
      this.loading = true;

      try {
        let product = await api.products.getById(this.$route.params.id);
        product.categoryIds = {};
        (product.product_categories || []).forEach((category) => {
          product.categoryIds[category.id] = true;
        });
        delete product.productCategories;

        const tagIds = (product.tags || []).map((t) => t.id);
        const videoUrls = Array.isArray(product.video_urls)
          ? [...product.video_urls]
          : [];

        this.form = {
          sku: product.sku || "",
          name: product.name || "",
          description: product.description || "",
          unit_price: product.unit_price,
          categoryIds: product.categoryIds,
          slug: product.slug || "",
          is_published: !!product.is_published,
          weight_grams: product.weight_grams,
          length_cm: product.length_cm,
          width_cm: product.width_cm,
          height_cm: product.height_cm,
          allow_backorder: !!product.allow_backorder,
          tag_ids: tagIds,
          video_urls: videoUrls,
        };
      } catch (error) {
        console.log(error);
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      if (!this.isEditing) {
        this.createProduct();
      } else {
        this.updateProduct();
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.form.sku) {
        this.errors["sku"] = ["Campo requerido"];
      }

      if (!this.form.name) {
        this.errors["name"] = ["Campo requerido"];
      }

      if (!this.form.description) {
        this.errors["description"] = ["Campo requerido"];
      }

      if (!this.form.unit_price) {
        this.errors["unit_price"] = ["Campo requerido"];
      }

      if (this.form.slug && !/^[a-z0-9-]+$/.test(this.form.slug)) {
        this.errors["slug"] = [
          "Solo letras minúsculas, números y guiones.",
        ];
      }

      return Object.keys(this.errors).length === 0;
    },

    payload() {
      const out = { ...this.form };
      // Drop empty strings for optional numeric/string fields the backend prefers as nullable.
      if (out.slug === "") out.slug = null;
      if (out.weight_grams === "" || out.weight_grams === null) out.weight_grams = null;
      out.video_urls = (out.video_urls || []).filter((u) => u && u.trim() !== "");
      return out;
    },

    async createProduct() {
      this.loadingForm = true;

      try {
        const response = await api.products.create(this.payload());
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "productsIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },

    async updateProduct() {
      this.loadingForm = true;

      try {
        const response = await api.products.update(
          this.$route.params.id,
          this.payload()
        );
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "productsIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },
  },
};
</script>
